import { useContext, useEffect, useState } from "react";
// CONTEXTS
import { VendorsContext } from "../../../../contexts/vendors.context";
import { SelectedOpenPartOrdersContext } from "../../../../contexts/parts/selectedOpenPartOrders.part.context";
// HOOKS
import useOrderedParts from "../../../../hooks/parts/useOrderedParts";
import useReturnFormComponents from "../../../../hooks/other/useReturnFormComponents";
// CONSTANTS
import PAYMENT_METHODS from "../../../../constants/parts/paymentMethods.part.constant";
import ORDERED_PART_STATUSES from "../../../../constants/parts/orderedPartStatuses.part.constant";
import DEFAULT_ORDER_PART_FORM from "../../../../constants/forms/defaultOrderPart.form.constant";
import DefaultButton from "../../../buttons/default.button";

const OrderedPartForm = (props) => {
  const [isLoading, setIsLoading] = useState(false);
  const { createOrderedPart } = useOrderedParts();
  const { getFormComponents } = useReturnFormComponents();
  const { selectedOpenPartOrders, setSelectedOpenPartOrders } = useContext(
    SelectedOpenPartOrdersContext
  );
  const { vendors } = useContext(VendorsContext);
  const [formFields, setFormFields] = useState(DEFAULT_ORDER_PART_FORM);
  const {
    _id,
    name,
    tickets,
    vendor_part_number,
    description,
    vendor,
    status,
    ordered_date,
    po_number,
    quantity,
    each_price,
    shipping_price,
    total_price,
    payment_method,
    eta,
    tracking,
    notes,
  } = formFields;
  const handleChange = (e) => {
    e.preventDefault();
    setFormFields({ ...formFields, [e.target.name]: e.target.value });
  };
  const handleCopyClick = (e) => {
    e.preventDefault();
    const mapSelectedParts = selectedOpenPartOrders.map((part) => ({
      id: part._id,
      ticket: part.ticket_number,
      description: part.description,
      ticket_id: part.ticket_id,
    }));
    setFormFields({
      ...formFields,
      description: mapSelectedParts[0]?.description,
      tickets: mapSelectedParts,
    });
  };
  const handleMultiSelectChange = (e) => {
    e.preventDefault();
    const { name, id, ticket, description, reset, ticket_id } =
      e.target.dataset;
    if (reset) return setFormFields({ ...formFields, [name]: [] });
    const options = formFields[name];
    // If option is already selected, remove it
    // Else add it to the array
    if (options?.find((v) => v.id === id)) {
      setFormFields({
        ...formFields,
        [name]: options.filter((v) => v.id !== id),
      });
    } else {
      setFormFields({
        ...formFields,
        description: description,
        [name]: [...options, { id: id, ticket, ticket_id }],
      });
    }
  };
  useEffect(() => {
    const totalPrice = +(+quantity * +each_price + +shipping_price);
    setFormFields({
      ...formFields,
      total_price: totalPrice.toFixed(2),
    });
  }, [quantity, each_price, shipping_price]);

  const form = [
    {
      type: "text",
      props: {
        label: "Name",
        value: name,
        name: "name",
        onChange: handleChange,
        className: "w-96",
      },
    },
    {
      type: "selection",
      props: {
        label: "Status",
        value: status,
        name: "status",
        options: ORDERED_PART_STATUSES,
        classNames: { container: "w-96" },
        onChange: handleChange,
      },
    },
    {
      type: "selection",
      props: {
        label: "Vendor",
        value: vendor,
        name: "vendor",
        classNames: { container: "w-96" },
        options: vendors.map((v) => ({ value: v._id, label: v.name })),
        onChange: handleChange,
      },
    },
    {
      type: "text",
      props: {
        label: "Vendor Part #",
        value: vendor_part_number,
        name: "vendor_part_number",
        onChange: handleChange,
        className: "w-96",
      },
    },
    {
      type: "text",
      props: {
        label: "Part Description",
        value: description,
        name: "description",
        onChange: handleChange,
        className: "w-96",
      },
    },
    {
      type: "text",
      props: {
        label: "PO Number",
        value: po_number,
        name: "po_number",
        onChange: handleChange,
        className: "w-96",
      },
    },
    {
      type: "multiSelect",
      props: {
        label: "Tickets",
        name: "tickets",
        value: tickets,
        childValue: "ticket",
        parentId: _id,
        multiple: true,
        options: selectedOpenPartOrders.map((part) => ({
          id: part._id,
          label: `${part.ticket_number}: ${part.description}`,
          ticket: part.ticket_number,
          description: part.description,
          ticket_id: part?.ticket_id,
        })),
        onChange: handleMultiSelectChange,
        handleCopyClick: handleCopyClick,
      },
    },

    {
      type: "number",
      props: {
        label: "Quantity",
        value: quantity,
        name: "quantity",
        onChange: handleChange,
        className: "w-48",
      },
    },
    {
      type: "number",
      props: {
        label: "Price/Each",
        value: each_price,
        name: "each_price",
        onChange: handleChange,
        className: "w-48",
      },
    },
    {
      type: "number",
      props: {
        label: "Shipping Price",
        value: shipping_price,
        name: "shipping_price",
        onChange: handleChange,
        className: "w-48",
      },
    },
    {
      type: "number",
      props: {
        label: "Total Price",
        value: total_price,
        name: "total_price",
        onChange: handleChange,
        className: "w-48",
        readOnly: true,
      },
    },
    {
      type: "selection",
      props: {
        label: "Payment Method",
        value: payment_method,
        name: "payment_method",
        options: PAYMENT_METHODS,
        onChange: handleChange,
        classNames: { container: "w-48" },
      },
    },

    {
      type: "text",
      props: {
        label: "Tracking",
        value: tracking,
        name: "tracking",
        onChange: handleChange,
        className: "w-96",
      },
    },
    {
      type: "date",
      props: {
        label: "ETA",
        value: eta,
        name: "eta",
        onChange: handleChange,
        className: "w-48",
      },
    },
    {
      type: "date",
      props: {
        label: "Ordered Date",
        value: ordered_date,
        name: "ordered_date",
        onChange: handleChange,
        className: "w-48",
      },
    },
    {
      type: "text",
      props: {
        label: "Notes",
        value: notes,
        name: "notes",
        onChange: handleChange,
      },
    },
  ];
  const handleSave = async (e) => {
    e.preventDefault();
    try {
      if (isLoading) return;
      if (+quantity <= 0) return alert("Please enter a quantity");
      if (!each_price) return alert("Please enter a price");
      if (!description) return alert("Please enter a part description");
      if (!ordered_date) return alert("Please enter an ordered date");
      setIsLoading(true);
      const data = await createOrderedPart(formFields);
      alert(`Created Ordered Part: ${data?.createOrderedPart?.description}`);
      setFormFields(DEFAULT_ORDER_PART_FORM);
      setIsLoading(false);
      setSelectedOpenPartOrders([
        ...selectedOpenPartOrders.filter(
          (part) => !tickets.find((t) => t.id === part._id)
        ),
      ]);
    } catch (error) {
      setIsLoading(false);
      console.log({ error });
      alert(error.message);
    }
  };
  return (
    <form className="w-[90%] border-animate rounded-2xl shadow-lg  flex flex-col items-center gap-4 p-8 bg-gray-200">
      <h1 className="font-ubuntuBold">Create Ordered Part</h1>
      <div className="flex justify-end w-full px-4 py-2">
        <DefaultButton handleClick={handleSave} loading={isLoading}>
          Save
        </DefaultButton>
      </div>
      <div className="flex flex-wrap gap-4 justify-evenly">
        {getFormComponents({ fields: form })}
      </div>
    </form>
  );
};

export default OrderedPartForm;
